import { CommonModule } from '@angular/common';
import {
	Component,
	computed,
	inject,
	input,
	Signal,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { ISbStoryData } from '@storyblok/js';
import { DynamicModule, InputsType } from 'ng-dynamic-component';
import { combineLatest, from, map, switchMap } from 'rxjs';

import { SkeletonComponent } from '@valk-nx/components/ui-skeleton/src/lib/skeleton';
import { LANGUAGE, Language } from '@valk-nx/core/lib/core';
import {
	DATE_FORMAT,
	TIME_INPUT_FORMAT,
	TIME_OUTPUT_FORMAT,
} from '@valk-nx/core/lib/date/date-format';
import { CarouselComponent as SbCarouselComponent } from '@valk-nx/storyblok/components/carousel/src/lib/carousel';
import { StoryblokSearchableListSectionComponent } from '@valk-nx/storyblok/components/searchable-list-section/src/lib/searchable-list-section';
import { ExtendedPersuadeCardInputs } from '@valk-nx/storyblok/components/searchable-list-section/src/lib/searchable-list-section.interface';
import { SectionComponent } from '@valk-nx/storyblok/components/section/src/lib/section';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { buildEventIconList } from '@valk-nx/storyblok-helpers/src/lib/general/icon-list.helper';
import {
	removeParameters,
	removeTrailingSlash,
} from '@valk-nx/storyblok-helpers/src/lib/general/sitemap.helper';
import { EventData } from '@valk-nx/storyblok-services/src/lib/services/content.interface';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import {
	ISbComponentType,
	ISbStoryBlok,
	TranslatedSlug,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.types';

const Components = {
	carousel: SbCarouselComponent,
	section: SectionComponent,
};

@Component({
	selector: 'sb-event-overview',
	templateUrl: './event-overview.component.html',
	imports: [
		CommonModule,
		DynamicModule,
		StoryblokSearchableListSectionComponent,
		SkeletonComponent,
	],
})
export class EventOverviewComponent extends StoryblokRootDirective {
	private readonly sbService = inject(StoryblokService);
	private readonly contentService = inject(ContentService);
	private readonly router = inject(Router);
	private readonly rootLanguage = inject<Language>(LANGUAGE);
	private readonly translate = inject(TranslateService);

	content = input.required<ISbStoryBlok[] | undefined>();

	components: typeof Components = Components;

	contentInputs = computed<(InputsType & { component: string })[]>(
		() =>
			this.content()?.map((blok) => ({
				...blok,
				component: blok['component'] ?? '',
			})) || [],
	);

	currentUrl = signal('');
	events = signal<EventData[]>([]);

	dateFormat = DATE_FORMAT;
	timeInputFormat = TIME_INPUT_FORMAT;
	timeOutputFormat = TIME_OUTPUT_FORMAT;

	readonly availableEvents: Signal<ExtendedPersuadeCardInputs[]> = computed(
		() => {
			if (this.events().length === 0) {
				return [];
			}

			return this.events().map((event) => {
				return {
					content: event.shortDescription,
					ctaText: this.translate.instant(
						'storyblok.pages.event-overview.card-cta',
					),
					ctaUrl: `${this.currentUrl()}/${event.slug}`,
					imageVariant: 'single' as const,
					images: event.images,
					price: event.price,
					showDisabledText: false,
					title: event.name,
					iconList: buildEventIconList(event, {
						dateFormat: this.dateFormat,
						timeInputFormat: this.timeInputFormat,
						timeOutputFormat: this.timeOutputFormat,
						translateFn: (key) => this.translate.instant(key),
					}),
				};
			});
		},
	);

	readonly listTitle = [
		{
			_uid: '',
			size: 'md',
			title: this.translate.instant(
				'storyblok.pages.event-overview.available-events',
			),
			component: 'runningHead',
		},
	];

	constructor() {
		super();

		this.sbService.translatedSlugs$
			.pipe(
				takeUntilDestroyed(),
				map((translatedSlugs) => this.extractPathInfo(translatedSlugs)),
				switchMap(({ path, language }) =>
					combineLatest([
						from(
							this.sbService.getStoriesByQuery(
								{
									starts_with: path,
									excluding_slugs: path,
									per_page: 100,
								},
								language,
							),
						),
						this.contentService.getEvents(language),
					]),
				),
			)
			.subscribe(([storiesResponse, eventsResponse]) =>
				this.handleSideEffects(
					storiesResponse.data.stories,
					eventsResponse.data,
				),
			);
	}

	private extractPathInfo(translatedSlugs: TranslatedSlug[]): {
		path: string;
		language: Language;
	} {
		const { url } = this.router;
		this.currentUrl.set(removeTrailingSlash(removeParameters(url)));

		const selectedSlug =
			translatedSlugs.find((x) => x.selected) ?? translatedSlugs[0];
		const path =
			selectedSlug.lang.toLowerCase() === this.rootLanguage
				? selectedSlug.path
				: selectedSlug.path.substring(3);

		return {
			path,
			language: selectedSlug.lang.toLowerCase() as Language,
		};
	}

	private handleSideEffects(
		storyblokEvents: ISbStoryData<
			ISbComponentType<string> & Record<string, unknown>
		>[],
		availableEvents: EventData[],
	) {
		const events = availableEvents.filter((availableEvent) =>
			storyblokEvents.some(
				(storyblokEvent) =>
					storyblokEvent.full_slug.split('/').pop() ===
					availableEvent.slug,
			),
		);

		this.events.set(events);
	}
}
